import { DBT_CAUSE_CODES, type DbtCauseCode } from "./dbtCauses";
import {
  EXTRACT_CAUSE_CODES,
  LOAD_CAUSE_CODES,
  PLATFORM_CAUSE_CODES,
  TEXT_CAUSE_CODES,
  type ExtractCauseCode,
  type LoadCauseCode,
  type PlatformCauseCode,
  type TextCauseCode,
} from "./importCauses";

/**
 * Who acts on a failure (reliability 1.4, #824; `docs/requirements/slo-canary.md` R3, ADR 0014
 * decision 4): the customer, Skypoint, either (`unknown`), or no one (`neither`, a refusal on
 * purpose). One closed function of a cause's source and code, exhaustive over every cause list, so
 * a code added to any list without a row here fails to compile (the `satisfies Record<...>` below)
 * and `whoActs.test.ts` fails too.
 *
 * The Level A success rate leaves out `customer` and `neither`; `unknown` counts against Skypoint
 * (conservative). The table is a judgement: #815's registry takes it over unchanged, and a row
 * moves there with a reason.
 *
 * Sources: `connector`, `load` and `platform` are the import cause sources (`IMPORT_CAUSE_SOURCES`;
 * the extract's codes are stored under `connector`), `dbt` the transform's (`dbtCauses.ts`), and
 * `push` the push job's two exit codes (`runner/push/spec.ts`). The source is part of the key
 * because the nine text codes sit in both the connector and the load lists.
 */

export const WHO_ACTS = ["customer", "skypoint", "unknown", "neither"] as const;
export type WhoActs = (typeof WHO_ACTS)[number];

export const WHO_ACTS_SOURCES = ["connector", "load", "platform", "dbt", "push"] as const;
export type WhoActsSource = (typeof WHO_ACTS_SOURCES)[number];

/**
 * The push job's closed failure codes (`runner/push/spec.ts` `PUSH_FAILED`, `PUSH_SIZE_MISMATCH`).
 * Repeated here because `shared/` never imports the runner; `whoActs.test.ts` pins them equal.
 */
export const PUSH_CAUSE_CODES = ["PUSH_FAILED", "PUSH_SIZE_MISMATCH"] as const;
export type PushCauseCode = (typeof PUSH_CAUSE_CODES)[number];

/** The nine text categories: read from text, not from a cause, so either side could own them. */
const TEXT_ROWS = Object.fromEntries(TEXT_CAUSE_CODES.map((code) => [code, "unknown"])) as Record<
  TextCauseCode,
  WhoActs
>;

const CONNECTOR_ROWS = {
  ...TEXT_ROWS,
  source_login_rejected: "customer",
  sql_sign_in_rejected: "customer",
  source_secret_unreadable: "customer",
  source_unreachable: "customer",
  source_database_offline: "customer",
  source_database_not_opened: "customer",
  source_throttled: "customer",
  source_config_invalid: "customer",
  source_host_key_changed: "customer",
  google_access_not_granted: "customer",
  // Skypoint's identity binding into Google, never a setting the customer can change.
  google_sign_in_failed: "skypoint",
  // The Google API switched off on Skypoint's Google Cloud project.
  google_setup_incomplete: "skypoint",
  google_service_account_invalid: "customer",
  // #2428: the report a person defined; they edit or add it again.
  report_definition_invalid: "customer",
  report_definition_missing: "customer",
  table_permission_denied: "customer",
  table_missing: "customer",
  column_missing: "customer",
  object_name_unreadable: "customer",
  source_cross_database_denied: "customer",
  view_query_failed: "customer",
  column_type_unsupported: "skypoint",
  file_parse_failed: "customer",
  read_interrupted: "unknown",
} as const satisfies Record<ExtractCauseCode, WhoActs>;

const LOAD_ROWS = {
  ...TEXT_ROWS,
  warehouse_login_rejected: "customer",
  warehouse_role_rejected: "customer",
  warehouse_unavailable: "customer",
  warehouse_object_denied: "customer",
  column_narrowed: "customer",
  load_mode_mismatch: "customer",
  table_name_conflict: "customer",
  // A table this connection did not create holds the name: the customer renames or removes it.
  target_not_ours: "customer",
  // Owner 2026-10-31: a table created with the old `CDM` column names, which the customer drops.
  old_column_names: "customer",
  // #924: an empty field of the destination, which an admin fills in (Settings, Destination).
  destination_incomplete: "customer",
  // Per-tenant storage, identities and the internal stage are Skypoint's (ADR 0010, 0013), and
  // `rows_short` is skyData's own load disagreeing with its own manifest.
  stage_credential_rejected: "skypoint",
  push_failed: "skypoint",
  stage_conflict: "skypoint",
  load_not_ready: "skypoint",
  rows_short: "skypoint",
} as const satisfies Record<LoadCauseCode, WhoActs>;

const PLATFORM_ROWS = {
  // A state the customer's own action put the connection in.
  connection_untested: "customer",
  connection_not_ready: "customer",
  // The job platform is Skypoint's.
  job_not_started: "skypoint",
  run_timed_out: "skypoint",
  job_lost: "skypoint",
  run_overlapped: "skypoint",
  // A PlatformAdmin disabled the tenant on purpose: the run was refused, not broken.
  tenant_disabled: "neither",
  // #787: the tenant's own resources are still being set up (ADR 0010), Skypoint's to finish.
  tenant_not_ready: "skypoint",
} as const satisfies Record<PlatformCauseCode, WhoActs>;

const DBT_ROWS = {
  // The customer's dbt project, models, tests or warehouse objects.
  COLUMN_NOT_FOUND: "customer",
  RELATION_NOT_FOUND: "customer",
  SQL_SYNTAX_ERROR: "customer",
  VALUE_CONVERSION_FAILED: "customer",
  NO_CURRENT_DATABASE: "customer",
  MODEL_COMPILATION_ERROR: "customer",
  UPSTREAM_FAILED: "customer",
  TEST_UNIQUE_FAILED: "customer",
  TEST_NOT_NULL_FAILED: "customer",
  TEST_RELATIONSHIPS_FAILED: "customer",
  TEST_ACCEPTED_VALUES_FAILED: "customer",
  TEST_SINGULAR_FAILED: "customer",
  TEST_GENERIC_FAILED: "customer",
  REF_NOT_FOUND: "customer",
  SOURCE_NOT_FOUND: "customer",
  PROJECT_PARSE_ERROR: "customer",
  DUPLICATE_RESOURCE: "customer",
  SELECTOR_NOT_VALID: "customer",
  MODEL_TARGETS_LANDED_TABLE: "customer",
  PHASE_CONFIG: "customer",
  PHASE_COMPILE: "customer",
  PHASE_STALE: "customer",
  // The dbt image and the network to the warehouse are Skypoint's. `WAREHOUSE_UNREACHABLE` and
  // `STATEMENT_TIMEOUT` are the rows a reviewer may argue about: in a per-tenant environment the
  // first of each has been Skypoint's network (R3).
  DBT_RUNTIME_ERROR: "skypoint",
  WAREHOUSE_UNREACHABLE: "skypoint",
  STATEMENT_TIMEOUT: "skypoint",
  PHASE_INFRA: "skypoint",
  PHASE_INTERNAL: "skypoint",
  // Read from a phase or from text: the warehouse or the repository, either side.
  WAREHOUSE_ERROR: "unknown",
  UNCLASSIFIED: "unknown",
  PHASE_AUTH: "unknown",
  PHASE_CLONE: "unknown",
  PHASE_DEPS: "unknown",
  PHASE_RUN: "unknown",
  PHASE_TEST: "unknown",
  PHASE_TIMEOUT: "unknown",
} as const satisfies Record<DbtCauseCode, WhoActs>;

const PUSH_ROWS = {
  PUSH_FAILED: "skypoint",
  PUSH_SIZE_MISMATCH: "skypoint",
} as const satisfies Record<PushCauseCode, WhoActs>;

/** Every row, by source. Exported for the exhaustiveness test only. */
export const WHO_ACTS_TABLE: Readonly<Record<WhoActsSource, Readonly<Record<string, WhoActs>>>> = {
  connector: CONNECTOR_ROWS,
  load: LOAD_ROWS,
  platform: PLATFORM_ROWS,
  dbt: DBT_ROWS,
  push: PUSH_ROWS,
};

/** The code lists each source is exhaustive over. Exported for the exhaustiveness test only. */
export const WHO_ACTS_CODE_LISTS: Readonly<Record<WhoActsSource, readonly string[]>> = {
  connector: EXTRACT_CAUSE_CODES,
  load: LOAD_CAUSE_CODES,
  platform: PLATFORM_CAUSE_CODES,
  dbt: DBT_CAUSE_CODES,
  push: PUSH_CAUSE_CODES,
};

/**
 * #1674: `PHASE_CLONE` stays `unknown` by its code alone (the network or the repository, either
 * side), but these closed reasons (`cloneReason`, read in the image) say it is the customer's
 * repository address, branch or token, which only the customer can change.
 */
const CUSTOMER_CLONE_REASONS: ReadonlySet<string> = new Set([
  "ref_not_found",
  "repo_unreadable",
  "auth",
]);

/** Who acts on a cause object: its source, code and, on a dbt checkout, its closed reason. */
export function whoActsOfCause(cause: {
  source: string;
  code: string;
  cloneReason?: string;
}): WhoActs {
  return whoActs(cause.source, cause.code, cause.cloneReason);
}

/**
 * Who acts on `code` from `source`. A source or code outside the lists (an older row, a code a
 * newer runner wrote) is `unknown`: it counts against Skypoint, never silently left out.
 */
export function whoActs(source: string, code: string, cloneReason?: string | null): WhoActs {
  // #1674: a failed checkout whose reason is the customer's repository, branch or token.
  if (
    source === "dbt" &&
    code === "PHASE_CLONE" &&
    cloneReason &&
    CUSTOMER_CLONE_REASONS.has(cloneReason)
  ) {
    return "customer";
  }
  if (!Object.hasOwn(WHO_ACTS_TABLE, source)) return "unknown";
  const rows = WHO_ACTS_TABLE[source as WhoActsSource];
  return Object.hasOwn(rows, code) ? (rows[code] as WhoActs) : "unknown";
}

/**
 * The customer-facing words by who acts (R6.5; PR 6 puts them in the renderer, the run page's cause
 * block and the `failed_run` incident). `customer` keeps each code's own fix sentence and `neither`
 * keeps today's text, so neither has a sentence here.
 */
export const SKYPOINT_LOOKING_INTO_IT = "Skypoint is looking into it";
export const WHO_ACTS_SKYPOINT_NEXT_STEP =
  "Skypoint is looking into it. You do not need to change anything.";
/** #839: beside a Retry button on a cause Skypoint acts on, so the button makes sense. */
export const WHO_ACTS_SKYPOINT_RETRY_HINT =
  "You can retry the failed tables now. Skypoint is also looking into it.";
/** #1465: the same hint where table runs are off and the retry is the run's. */
export const WHO_ACTS_SKYPOINT_RUN_RETRY_HINT =
  "You can retry the run now. Skypoint is also looking into it.";
export const WHO_ACTS_UNKNOWN_NEXT_STEP =
  "Skypoint is checking whether this is on our side. If it is your source or warehouse, the next step will appear here.";

/**
 * The "Next step" of a cause by who acts on it (#829, R6.5), the one rule the renderer, the run
 * page's cause block and its failure summary apply: Skypoint's words for `skypoint` (the per-code
 * fix is dropped), and the cause's own fix (or none) for every other value.
 *
 * `unknown` keeps today's fix on purpose (owner direction on #839, 2026-10-11): the nine text
 * codes read `unknown` until #815's registry moves them, and nothing a customer sees today may get
 * vaguer meanwhile. `WHO_ACTS_UNKNOWN_NEXT_STEP` stays defined for #815 to use.
 */
export function nextStepFor(
  source: string,
  code: string,
  fix: string | null,
  /** #1674 review: a dbt checkout's closed reason, read by `whoActs` like the code. */
  cloneReason?: string | null,
): string | null {
  return whoActs(source, code, cloneReason) === "skypoint" ? WHO_ACTS_SKYPOINT_NEXT_STEP : fix;
}

/**
 * #1084: whether a cause must be fixed before a retry can help. A cause the customer acts on whose
 * own action is not a retry (a missing table, a refused sign-in, an empty destination field) fails
 * again until the customer changes something, so its action leads: the run's outcome and the
 * step's panel draw it as the primary button and every retry they offer as a secondary one after
 * it. No retry is taken away: a run of several tables or connections may hold other causes a retry
 * does fix, and once the customer has fixed this one the retry is the next step. Skypoint's and
 * unknown causes, and a cause whose action IS the retry, keep the retry first.
 */
export function fixBeforeRetry(
  source: string,
  code: string,
  action: string,
  /** #1674 review: a dbt checkout's closed reason, read by `whoActs` like the code. */
  cloneReason?: string | null,
): boolean {
  if (action === "retry_failed_tables") return false;
  if (FIX_INSIDE_SKYDATA.some(([s, c]) => s === source && c === code)) return true;
  return whoActs(source, code, cloneReason) === "customer";
}

/**
 * #1084: causes whose fix is inside skyData although `whoActs` reads them `unknown` (a text code,
 * read from words until #815's registry moves it): the extract's text-tier `missing_object` says
 * the same next step as `table_missing`, so it leads the same way. `column_missing` is a typed
 * extract code the customer already acts on, so it needs no row here.
 */
export const FIX_INSIDE_SKYDATA: ReadonlyArray<readonly [string, string]> = [
  ["connector", "missing_object"],
];
